/**
 * Framework-free schema.org JSON-LD builders shared by BOTH SEO emit paths.
 *
 * This is the single home for the structured-data "completeness" logic the DCS
 * SEO factory needs: a cross-linked `@graph` knowledge spine (Organization /
 * WebSite / LocalBusiness), `BreadcrumbList`, `BlogPosting`, `FAQPage`, and the
 * HONEST `Review` / `aggregateRating` injection. Both call sites consume it:
 *
 *   - the Vue-SPA per-route emitter (`buildHeadTags` in `./headTags.ts`), and
 *   - the VitePress `transformPageData` factory (`./vitepressTransform.ts`).
 *
 * Every export here is a PURE function of plain inputs (no Vue, no Vite, no
 * filesystem) returning plain `schema.org` objects, so the two paths stay
 * byte-identical and the builder logic is never duplicated.
 *
 * ── HONESTY (non-negotiable) ────────────────────────────────────────────────
 * `Review`, `aggregateRating`, and `FAQPage` are emitted ONLY from REAL data:
 *   • Review/aggregateRating come from `.dcs/content.yaml` review items that
 *     carry a numeric `rating`, a non-empty `text`, and an `authorName`.
 *   • FAQPage comes from a structured Q&A source (frontmatter `faq: [{q,a}]` or
 *     `.dcs/faq.yaml`) where each entry has a non-empty question AND answer.
 * When the real source is missing or empty the builder short-circuits to an
 * empty result — it NEVER synthesises a placeholder rating, review, or Q&A. A
 * fabricated rating is a legal + trust risk; this module makes fabrication
 * impossible by construction (no defaults, no invented counts).
 */

import type { GlobalSeoConfig, SeoSchemaConfig } from '../types/seo'

const SCHEMA_CONTEXT = 'https://schema.org'

/** A plain schema.org object (already shaped for JSON-LD serialisation). */
export type SchemaObject = Record<string, unknown>

/**
 * The schema.org `@type`s that, when present in `global.schemas`, are treated as
 * the site's LocalBusiness node and ABSORBED into the `@graph` spine (promoted
 * with an `@id` + `parentOrganization` ref) rather than emitted as a second,
 * unlinked copy. This is the union of `LocalBusiness` and its common subtypes
 * used across DCS sites (Iron Oak `HomeAndConstructionBusiness`, kept
 * `MedicalBusiness`, ktbraunlaw `LegalService`).
 */
const LOCAL_BUSINESS_TYPES = new Set<string>([
  'LocalBusiness',
  'HomeAndConstructionBusiness',
  'GeneralContractor',
  'HVACBusiness',
  'Plumber',
  'Electrician',
  'RoofingContractor',
  'HousePainter',
  'MovingCompany',
  'MedicalBusiness',
  'Dentist',
  'Physician',
  'MedicalClinic',
  'LegalService',
  'Attorney',
  'Notary',
  'AccountingService',
  'FinancialService',
  'InsuranceAgency',
  'RealEstateAgent',
  'Store',
  'ProfessionalService',
  'AutomotiveBusiness',
  'AutoRepair',
  'BeautySalon',
  'HairSalon',
  'HealthAndBeautyBusiness',
  'FoodEstablishment',
  'Restaurant',
])

/** Return true when a schema `@type` is a LocalBusiness (sub)type. */
export function isLocalBusinessType(type: string | undefined): boolean {
  return !!type && LOCAL_BUSINESS_TYPES.has(type)
}

/**
 * A real review item, mirroring the shape stored in `.dcs/content.yaml` under a
 * `reviews.<key>.items` array (see `useReviewContent`). Only the fields the
 * honest Review/aggregateRating builder needs are typed here; extra fields are
 * ignored.
 */
export interface ReviewSource {
  rating?: unknown
  text?: unknown
  authorName?: unknown
  date?: unknown
  locationName?: unknown
}

/** A structured FAQ entry: `{ q, a }` (frontmatter) — honesty-gated. */
export interface FaqSource {
  q?: unknown
  a?: unknown
  /** Alternate keys some sources use (`question`/`answer`). */
  question?: unknown
  answer?: unknown
}

/** A single breadcrumb hop along the route to the current page. */
export interface BreadcrumbCrumb {
  /** Human-readable name (e.g. `Home`, `Services`, the post title). */
  name: string
  /** Absolute URL for this hop. */
  item: string
}

/** Blog-post metadata used to build a `BlogPosting`, from the SPA or VitePress. */
export interface BlogMeta {
  /** The headline / post title. */
  headline?: string
  /** ISO-ish publish date as authored (granularity preserved, e.g. `2026-01`). */
  datePublished?: string
  /** ISO-ish modified date, if distinct. */
  dateModified?: string
  /** Absolute canonical URL of the post (becomes `mainEntityOfPage`). */
  url?: string
  /** Header/social image URL for the post. */
  image?: string
  /** Short description / excerpt. */
  description?: string
}

// =============================================================================
// @id derivation
// =============================================================================

function trimSlash(url: string): string {
  return url.replace(/\/+$/, '')
}

/** Stable `@id` anchors derived from the site URL (no new YAML required). */
export function graphIds(siteUrl: string): {
  organization: string
  website: string
  localBusiness: string
} {
  const base = trimSlash(siteUrl)
  return {
    organization: `${base}/#organization`,
    website: `${base}/#website`,
    localBusiness: `${base}/#localbusiness`,
  }
}

// =============================================================================
// social → sameAs
// =============================================================================

/**
 * Derive an `sameAs` array of absolute profile URLs from the global `social`
 * block. Returns `[]` when nothing is configured (so the key is omitted, never
 * emitted empty).
 */
export function deriveSameAs(global: GlobalSeoConfig): string[] {
  const s = global.social
  if (!s) return []
  const out: string[] = []
  const push = (u: string | undefined) => {
    if (u && u.trim()) out.push(u.trim())
  }
  // Each handle may already be a full URL or a bare slug/handle.
  if (s.facebook) push(asUrl(s.facebook, 'https://www.facebook.com/'))
  if (s.instagram) push(asUrl(stripAt(s.instagram), 'https://www.instagram.com/'))
  if (s.linkedin) push(asUrl(s.linkedin, 'https://www.linkedin.com/in/'))
  if (s.youtube) push(asUrl(s.youtube, 'https://www.youtube.com/'))
  if (s.github) push(asUrl(stripAt(s.github), 'https://github.com/'))
  if (s.twitter) push(asUrl(stripAt(s.twitter), 'https://twitter.com/'))
  return out
}

function stripAt(handle: string): string {
  return handle.startsWith('@') ? handle.slice(1) : handle
}

function asUrl(value: string, base: string): string {
  if (/^https?:\/\//i.test(value)) return value
  return `${base}${value.replace(/^\/+/, '')}`
}

// =============================================================================
// LocalBusiness extraction
// =============================================================================

/**
 * Find the LocalBusiness-subtype entry in `global.schemas`, if any. This is the
 * NAP-complete node a site already hand-authors; the graph builder absorbs it
 * (rather than letting `generateJsonLd` emit a second, unlinked copy).
 *
 * @returns the matching `SeoSchemaConfig` and its index, or `null`.
 */
export function findLocalBusinessSchema(
  global: GlobalSeoConfig
): { schema: SeoSchemaConfig; index: number } | null {
  const schemas = global.schemas ?? []
  for (let i = 0; i < schemas.length; i++) {
    if (isLocalBusinessType(schemas[i]?.type)) {
      return { schema: schemas[i], index: i }
    }
  }
  return null
}

/**
 * The `PostalAddress` fields that together make a NAP a real, mappable address.
 * Order is the order a human writes one, which is the order the warning prints.
 */
export const REQUIRED_POSTAL_ADDRESS_FIELDS = [
  'streetAddress',
  'addressLocality',
  'addressRegion',
  'postalCode',
  'addressCountry',
] as const

/** A LocalBusiness node that CLAIMS an address but does not finish it. */
export interface PostalAddressGap {
  /** The LocalBusiness `@type` as authored (e.g. `HomeAndConstructionBusiness`). */
  businessType: string
  /** Required fields absent or blank, in `REQUIRED_POSTAL_ADDRESS_FIELDS` order. */
  missing: string[]
  /** Required fields actually supplied — what makes the node look answered. */
  present: string[]
}

/**
 * Report a LocalBusiness `address` that is PRESENT BUT PARTIAL.
 *
 * `buildGlobalGraph` spreads the hand-authored node's properties verbatim
 * (`...props`), so a `PostalAddress` carrying only a locality and a region ships
 * to crawlers exactly as authored, with nothing anywhere saying it is half an
 * address. Google matches a LocalBusiness to a place using the NAP; a partial
 * address is the shape that silently fails that match while every audit that
 * only asks "is there an address node?" stays green.
 *
 * ## Why an ABSENT address is not reported
 *
 * A service-area business (a contractor working out of a home, a mobile trade)
 * is *supposed* to omit `address` — that is Google's own guidance, and several
 * DCS fleet sites are exactly that. Warning there would be an accusation
 * levelled at correct configuration, and a warning people learn to ignore is
 * worth less than no warning. The defect is the CLAIM that is not finished, so
 * the trigger is an address node that exists and is incomplete.
 *
 * A non-object `address` (a plain string) is also skipped: it is unstructured,
 * not partial, and belongs to a different finding.
 *
 * @returns one gap per incomplete address, or `[]` when complete/absent.
 */
export function findPostalAddressGaps(global: GlobalSeoConfig): PostalAddressGap[] {
  const found = findLocalBusinessSchema(global)
  if (!found) return []

  const address = (found.schema.properties ?? {})['address']
  if (!address || typeof address !== 'object' || Array.isArray(address)) return []

  const fields = address as Record<string, unknown>
  const missing: string[] = []
  const present: string[] = []
  for (const key of REQUIRED_POSTAL_ADDRESS_FIELDS) {
    const value = fields[key]
    const filled = typeof value === 'string' ? value.trim().length > 0 : value != null
    ;(filled ? present : missing).push(key)
  }
  if (missing.length === 0) return []

  return [{ businessType: found.schema.type ?? 'LocalBusiness', missing, present }]
}

/**
 * Render the partial-address warning. Names the type, what is there, and what is
 * not — an operator must be able to act without re-deriving the finding, and the
 * "or drop the address entirely" branch is stated because for a service-area
 * business that is the CORRECT fix, not a workaround.
 */
export function formatPostalAddressGapReport(gaps: PostalAddressGap[]): string {
  const lines: string[] = []
  for (const gap of gaps) {
    lines.push(
      `[dcs-seo] LocalBusiness (${gap.businessType}) emits a PARTIAL PostalAddress: ` +
        `missing ${gap.missing.join(', ')} (present: ${gap.present.join(', ') || 'nothing'}). ` +
        `A half-authored address ships to crawlers as if it were complete and will not ` +
        `match the business to a place. Fix: complete the address in .dcs/seo.yaml, or — ` +
        `if this is a service-area business with no public storefront — remove the ` +
        `address block entirely and rely on areaServed, which is the correct shape for it.`
    )
  }
  return lines.join('\n')
}

/**
 * True when `buildGlobalGraph` provides this schema canonically, so it must NOT
 * also be emitted standalone: the LocalBusiness subtype it folds in, or a global
 * `Organization` / `WebSite` (the graph emits cross-linked versions of both).
 *
 * Shared by both emit paths so the de-dup rule is identical.
 *
 * INVARIANT (C-609): every schema this returns `true` for must be ABSORBED by
 * `buildGlobalGraph` — its hand-authored properties merged into the corresponding
 * `@graph` node. Absorbing here without merging there is silent data loss: the
 * node is stripped from the per-schema emission and its properties never reach
 * the crawler (the C-418 defect this pairing now prevents).
 */
export function graphAbsorbs(schema: SeoSchemaConfig, global: GlobalSeoConfig): boolean {
  if (schema.type === 'Organization' || schema.type === 'WebSite') return true
  const found = findLocalBusinessSchema(global)
  return !!found && found.schema === schema
}

/**
 * All hand-authored `global.schemas` entries of an exact `@type`, in document
 * order. Plural because `graphAbsorbs` absorbs EVERY `Organization` / `WebSite`
 * entry — so the graph must merge every one of them or the extras vanish.
 */
export function findGlobalSchemasOfType(
  global: GlobalSeoConfig,
  type: string
): SeoSchemaConfig[] {
  return (global.schemas ?? []).filter((s) => s?.type === type)
}

/**
 * Keys the `@graph` OWNS and never takes from hand-authored `properties`.
 *
 * `@type` and `@id` are the node's identity: `@id` is the anchor the spine's
 * cross-links (`publisher`, `parentOrganization`) resolve against, so letting a
 * hand-authored property overwrite it would dangle those refs. `@context` belongs
 * on the `@graph` wrapper, not on a node inside it.
 */
const GRAPH_OWNED_KEYS = new Set(['@context', '@id', '@type'])

/**
 * ABSORB hand-authored schema `properties` into a derived `@graph` node, in
 * place. Mirrors the LocalBusiness absorption semantics (`...props` spread over
 * the derived fields): a hand-authored property OVERRIDES the derived value for
 * the same key, and any key the graph does not derive is ADDED verbatim. Only
 * the identity keys in `GRAPH_OWNED_KEYS` are refused.
 *
 * Key ORDER is preserved for derived keys (an override rewrites in place) and
 * new hand-authored keys append — so the baked JSON-LD stays diff-stable.
 */
function absorbSchemaProps(
  node: SchemaObject,
  props: Record<string, unknown> | undefined
): void {
  if (!props) return
  for (const [key, value] of Object.entries(props)) {
    if (GRAPH_OWNED_KEYS.has(key)) continue
    node[key] = value
  }
}

// =============================================================================
// Honest Reviews / aggregateRating
// =============================================================================

/** One normalised, REAL review (passed the honesty gate). */
export interface NormalisedReview {
  rating: number
  text: string
  authorName: string
  date?: string
  locationName?: string
}

/**
 * Keep ONLY real review items: a numeric `rating`, a non-empty `text`, and a
 * non-empty `authorName`. Anything missing any of the three is dropped (never
 * back-filled). Returns `[]` when nothing qualifies.
 */
export function filterRealReviews(items: ReviewSource[] | undefined): NormalisedReview[] {
  if (!Array.isArray(items)) return []
  const out: NormalisedReview[] = []
  for (const item of items) {
    if (!item || typeof item !== 'object') continue
    const ratingNum = Number((item as ReviewSource).rating)
    const text = typeof item.text === 'string' ? item.text.trim() : ''
    const authorName = typeof item.authorName === 'string' ? item.authorName.trim() : ''
    if (!Number.isFinite(ratingNum) || ratingNum <= 0) continue
    if (!text) continue
    if (!authorName) continue
    out.push({
      rating: ratingNum,
      text,
      authorName,
      ...(typeof item.date === 'string' && item.date.trim() ? { date: item.date.trim() } : {}),
      ...(typeof item.locationName === 'string' && item.locationName.trim()
        ? { locationName: item.locationName.trim() }
        : {}),
    })
  }
  return out
}

/** A `Review` schema.org node array + an `aggregateRating`, both honesty-gated. */
export interface ReviewSchemaParts {
  /** `Review` nodes (one per real item). Empty when no real reviews. */
  review: SchemaObject[]
  /** `AggregateRating` node, or `undefined` when no real reviews. */
  aggregateRating?: SchemaObject
}

/** The schema.org `bestRating`/`worstRating` bounds for DCS review ratings. */
const BEST_RATING = 5
const WORST_RATING = 1

/** Clamp a rating into `[WORST_RATING, BEST_RATING]` (F3). */
function clampRating(value: number): number {
  if (value < WORST_RATING) return WORST_RATING
  if (value > BEST_RATING) return BEST_RATING
  return value
}

/**
 * Build `Review[]` + `aggregateRating` from REAL review items only.
 *
 * Each `ratingValue` is CLAMPED to `[worstRating, bestRating]` (F3): a source
 * rating of 7 (or 0/-1) would otherwise emit an out-of-range, schema-invalid
 * value (and skew the aggregate). The aggregate mean is computed from the SAME
 * clamped values so the headline rating matches the displayed reviews.
 *
 * `aggregateRating.ratingValue` is the mean (rounded to one decimal),
 * `reviewCount`/`ratingCount` equal the real item count. When there are zero
 * real items, BOTH are omitted — never an invented rating or count.
 */
export function buildReviewSchemaParts(items: ReviewSource[] | undefined): ReviewSchemaParts {
  const real = filterRealReviews(items)
  if (real.length === 0) return { review: [] }

  const clamped = real.map((r) => ({ ...r, rating: clampRating(r.rating) }))

  const review: SchemaObject[] = clamped.map((r) => ({
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: r.rating,
      bestRating: BEST_RATING,
      worstRating: WORST_RATING,
    },
    author: { '@type': 'Person', name: r.authorName },
    reviewBody: r.text,
    ...(r.date ? { datePublished: r.date } : {}),
  }))

  const sum = clamped.reduce((acc, r) => acc + r.rating, 0)
  const mean = Math.round((sum / clamped.length) * 10) / 10

  const aggregateRating: SchemaObject = {
    '@type': 'AggregateRating',
    ratingValue: mean,
    reviewCount: clamped.length,
    ratingCount: clamped.length,
    bestRating: BEST_RATING,
    worstRating: WORST_RATING,
  }

  return { review, aggregateRating }
}

// =============================================================================
// hasCredential (trade / occupational license — honesty-gated)
// =============================================================================

/**
 * The schema.org `credentialCategory` for a trade / occupational license.
 * `hasCredential` → `EducationalOccupationalCredential` with this category is the
 * documented pattern for a professional license on an Organization /
 * LocalBusiness (see schema.org/EducationalOccupationalCredential).
 */
const LICENSE_CREDENTIAL_CATEGORY = 'license'

/**
 * Build a single `EducationalOccupationalCredential` node for a trade license
 * from the free-text `business.license` content key, or `undefined` when the key
 * is empty / whitespace / absent.
 *
 * Shape (schema.org-correct; mirrors the portal's `Credential` editor type in
 * `portal/src/lib/jsonld-schema-types.ts`):
 *
 *   { '@type': 'EducationalOccupationalCredential',
 *     credentialCategory: 'license',
 *     name: <verbatim business.license value> }
 *
 * HONESTY (non-negotiable, mirrors the Review/FAQ gates in this module): an
 * empty / whitespace / absent value returns `undefined` so the caller omits
 * `hasCredential` entirely — never an empty string, never a fabricated license.
 * The value is carried verbatim in `name` (the field is free text — a license
 * number, a "Licensed & Insured" phrase, or a credential name — so the
 * always-valid `name` slot is the safest home; nothing is parsed or invented).
 */
export function buildHasCredential(license: string | undefined): SchemaObject | undefined {
  const value = typeof license === 'string' ? license.trim() : ''
  if (!value) return undefined
  return {
    '@type': 'EducationalOccupationalCredential',
    credentialCategory: LICENSE_CREDENTIAL_CATEGORY,
    name: value,
  }
}

// =============================================================================
// Global @graph spine
// =============================================================================

/**
 * Build the cross-linked global knowledge graph as ONE JSON-LD object carrying
 * a `@graph` array: `Organization`, `WebSite` (publisher → org), and the site's
 * `LocalBusiness` node (parentOrganization → org), auto-derived from
 * `global.siteName` / `global.siteUrl` / `global.images.logo` / `global.social`.
 * Requires NO new YAML.
 *
 * ALL THREE nodes ABSORB their hand-authored `global.schemas[*]` counterpart
 * (C-609) — the properties are merged onto the derived node rather than
 * discarded, which is what makes the baked `@graph` a true SUPERSET of the
 * runtime per-schema emission that `graphAbsorbs` suppresses:
 *
 *   • LocalBusiness — its NAP/geo/hours/offers are preserved verbatim and the
 *     node is promoted with an `@id`; honest `review` + `aggregateRating` and a
 *     `hasCredential` license are merged in when supplied.
 *   • Organization — e.g. the fleet default's hand-authored `logo`, which
 *     `global.images.logo` does not set.
 *   • WebSite — e.g. a `description` or a `potentialAction` SearchAction.
 *
 * Precedence is uniform: hand-authored properties OVERRIDE the derived value for
 * the same key and ADD any key the graph does not derive, except the identity
 * keys (`@type`, `@id`) and the spine's cross-links (`publisher`,
 * `parentOrganization`), which the graph always owns so the `@id` refs resolve.
 *
 * Returns `[]` when there is no `siteUrl` (no stable `@id` anchor possible), so
 * the existing per-schema emission is left untouched for un-configured sites.
 *
 * @param opts.reviews REAL review items (from content.yaml) for the business
 *   node. Optional; when omitted/empty no Review/aggregateRating is added.
 * @param opts.license Free-text trade/occupational license (from the
 *   `business.license` content key). Optional; when empty/absent no
 *   `hasCredential` is added (honesty-gated — see `buildHasCredential`). Only
 *   ever attached to the LocalBusiness node, never to Organization/WebSite.
 */
export function buildGlobalGraph(
  global: GlobalSeoConfig,
  opts: { reviews?: ReviewSource[]; license?: string } = {}
): SchemaObject[] {
  const siteUrl = global.siteUrl ? trimSlash(global.siteUrl) : ''
  if (!siteUrl) return []

  const ids = graphIds(siteUrl)
  const name = global.siteName || ''
  const logo = global.images?.logo
  const sameAs = deriveSameAs(global)

  // ── Organization (absorb the hand-authored node, keep the derived identity) ─
  const organization: SchemaObject = {
    '@type': 'Organization',
    '@id': ids.organization,
    ...(name ? { name } : {}),
    url: `${siteUrl}/`,
    ...(logo ? { logo } : {}),
    ...(sameAs.length ? { sameAs } : {}),
  }
  // Hand-authored `Organization` properties override/extend the derived fields
  // (the fleet default authors a `logo` that `global.images.logo` never sets).
  for (const schema of findGlobalSchemasOfType(global, 'Organization')) {
    absorbSchemaProps(organization, schema.properties)
  }

  // ── WebSite (absorb the hand-authored node, keep the publisher cross-link) ──
  const website: SchemaObject = {
    '@type': 'WebSite',
    '@id': ids.website,
    url: `${siteUrl}/`,
    ...(name ? { name } : {}),
  }
  // Hand-authored `WebSite` properties (e.g. the site-onboard template's
  // `description` + `potentialAction` SearchAction) override/extend the derived
  // fields…
  for (const schema of findGlobalSchemasOfType(global, 'WebSite')) {
    absorbSchemaProps(website, schema.properties)
  }
  // …but the cross-link to the Organization is forced AFTER the merge, exactly
  // as `parentOrganization` is on the LocalBusiness node below: the spine is the
  // graph's to own, not the hand-authored node's to break.
  website.publisher = { '@id': ids.organization }

  const graph: SchemaObject[] = [organization, website]

  // ── LocalBusiness (absorb the hand-authored NAP node, add @id + parent) ────
  const found = findLocalBusinessSchema(global)
  if (found) {
    const props = found.schema.properties ?? {}
    const localBusiness: SchemaObject = {
      '@type': found.schema.type,
      '@id': ids.localBusiness,
      // Preserve the hand-authored NAP/geo/hours/offers verbatim…
      ...props,
      // …but ensure the cross-link to the Organization is present.
      parentOrganization: { '@id': ids.organization },
    }
    // Default url to the site root when the node doesn't set its own.
    if (localBusiness.url == null) localBusiness.url = `${siteUrl}/`

    // Honest reviews → merge Review[] + aggregateRating into the business node.
    const reviewParts = buildReviewSchemaParts(opts.reviews)
    if (reviewParts.review.length > 0) {
      localBusiness.review = reviewParts.review
      if (reviewParts.aggregateRating) {
        localBusiness.aggregateRating = reviewParts.aggregateRating
      }
    }

    // Trade/occupational license → hasCredential on the business node ONLY.
    // Honesty-gated: an empty/absent business.license emits nothing.
    const credential = buildHasCredential(opts.license)
    if (credential) {
      localBusiness.hasCredential = credential
    }

    graph.push(localBusiness)
  }

  return [
    {
      '@context': SCHEMA_CONTEXT,
      '@graph': graph,
    },
  ]
}

// =============================================================================
// BreadcrumbList
// =============================================================================

/**
 * Build a `BreadcrumbList` from an ordered trail of crumbs (Home → … → current).
 *
 * Honesty/cleanliness rules:
 *   • The home page (a trail of length ≤ 1, i.e. just "Home") emits NOTHING —
 *     a single-item breadcrumb is noise.
 *   • Positions are 1-based and contiguous.
 *
 * @returns a single-element array `[BreadcrumbList]`, or `[]` for the home page.
 */
export function buildBreadcrumbList(trail: BreadcrumbCrumb[]): SchemaObject[] {
  if (!Array.isArray(trail) || trail.length <= 1) return []

  const itemListElement = trail.map((crumb, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: crumb.name,
    item: crumb.item,
  }))

  return [
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'BreadcrumbList',
      itemListElement,
    },
  ]
}

/**
 * Derive a Home → … → current breadcrumb trail from a route path.
 *
 * Each path segment becomes a crumb; the segment label comes from
 * `titles[segmentPath]` (an absolute-route → title map) when present, else a
 * slug-derived Title Case of the segment (never blank).
 *
 * @param route     the page route, e.g. `/`, `/services`, `/blog/my-post`.
 * @param siteUrl   normalised site URL (no trailing slash).
 * @param titles    optional map of route → human title for intermediate hops
 *                  AND the leaf (e.g. `{ '/': 'Home', '/blog': 'Blog',
 *                  '/blog/my-post': 'My Post' }`). Missing entries fall back to
 *                  a slug-derived title.
 * @param homeName  label for the root crumb (default `Home`).
 */
export function breadcrumbTrailFromRoute(
  route: string,
  siteUrl: string,
  titles: Record<string, string> = {},
  homeName = 'Home'
): BreadcrumbCrumb[] {
  const base = trimSlash(siteUrl)
  const home: BreadcrumbCrumb = {
    name: titles['/'] || homeName,
    item: `${base}/`,
  }
  const normalised = (route || '/').split('?')[0].split('#')[0]
  if (normalised === '/' || normalised === '') return [home]

  const segments = normalised.replace(/^\/+/, '').replace(/\/+$/, '').split('/')
  const trail: BreadcrumbCrumb[] = [home]
  let acc = ''
  for (const seg of segments) {
    acc += `/${seg}`
    trail.push({
      name: titles[acc] || slugToTitle(seg),
      item: `${base}${acc}`,
    })
  }
  return trail
}

/** Title-case a slug segment (`my-post` → `My Post`); never blank. */
export function slugToTitle(slug: string): string {
  const cleaned = (slug || '').replace(/[-_]+/g, ' ').trim()
  if (!cleaned) return slug || ''
  return cleaned
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// =============================================================================
// BlogPosting
// =============================================================================

/**
 * Build a single `BlogPosting`.
 *
 * `author`/`publisher` are emitted as SELF-CONTAINED Organization nodes — each
 * carrying its `@id`, plus a concrete `name` and `url`. This is the F2 fix: a
 * bare `{ '@id': … }` ref DANGLES when `emitBlogPosting` runs WITHOUT the global
 * `@graph` (the VitePress path with `emitGraph: false`), because nothing then
 * defines the Organization node that `@id` points at. An inline node is valid
 * standalone AND still carries the `@id`, so when the `@graph` IS present a
 * consumer merges the two by `@id` (no duplication, no dangle) either way.
 *
 * `datePublished` granularity is preserved exactly as authored (e.g. a
 * year-month `2026-01` is NOT padded to a fabricated day).
 *
 * @param global Optional global config — supplies the publisher/author `name`.
 *   When omitted (or no `siteName`), the inline node still carries `@id` + `url`
 *   so the reference never dangles.
 * @returns `[BlogPosting]`, or `[]` when there is no headline (nothing to emit).
 */
export function buildBlogPosting(
  meta: BlogMeta,
  siteUrl: string,
  global?: GlobalSeoConfig
): SchemaObject[] {
  const headline = typeof meta.headline === 'string' ? meta.headline.trim() : ''
  if (!headline) return []

  const ids = graphIds(siteUrl)
  const base = trimSlash(siteUrl)
  const name = global?.siteName?.trim()

  // Self-contained publisher/author node: keeps the @id (graph-mergeable) AND
  // inlines name/url so it never dangles when no @graph spine is emitted.
  const orgNode: SchemaObject = {
    '@type': 'Organization',
    '@id': ids.organization,
    ...(name ? { name } : {}),
    ...(base ? { url: `${base}/` } : {}),
  }

  const node: SchemaObject = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BlogPosting',
    headline,
    author: orgNode,
    publisher: orgNode,
  }
  if (meta.description) node.description = meta.description
  if (meta.datePublished) node.datePublished = meta.datePublished
  if (meta.dateModified) node.dateModified = meta.dateModified
  if (meta.image) node.image = meta.image
  if (meta.url) node.mainEntityOfPage = meta.url

  return [node]
}

// =============================================================================
// FAQPage (honesty-gated)
// =============================================================================

/** One normalised, REAL FAQ pair (passed the honesty gate). */
export interface NormalisedFaq {
  question: string
  answer: string
}

/**
 * Keep ONLY real FAQ entries: a non-empty question AND a non-empty answer.
 * Tolerant of both `{ q, a }` and `{ question, answer }` shapes. Returns `[]`
 * when nothing qualifies (so no FAQPage is emitted).
 */
export function filterRealFaq(entries: FaqSource[] | undefined): NormalisedFaq[] {
  if (!Array.isArray(entries)) return []
  const out: NormalisedFaq[] = []
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue
    const q = pickString(entry.q ?? entry.question)
    const a = pickString(entry.a ?? entry.answer)
    if (!q || !a) continue
    out.push({ question: q, answer: a })
  }
  return out
}

function pickString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/**
 * Build a `FAQPage` from a STRUCTURED Q&A source ONLY (frontmatter `faq:` or a
 * `.dcs/faq.yaml`). A `<FaqPage />` Vue component is NOT a valid source — there
 * is no machine-readable Q&A to read, so this correctly emits nothing.
 *
 * @returns `[FAQPage]` with one `Question` per real entry, or `[]` when there is
 *   no valid structured Q&A.
 */
export function buildFaqPage(entries: FaqSource[] | undefined): SchemaObject[] {
  const real = filterRealFaq(entries)
  if (real.length === 0) return []

  return [
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'FAQPage',
      mainEntity: real.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ]
}

// =============================================================================
// content.yaml review lookup (tolerant of per-site key naming)
// =============================================================================

/**
 * The relevant slice of `.dcs/content.yaml`: flat dotted keys live under
 * `global` and per-page maps. Reviews are stored under `reviews.<key>.items`
 * (e.g. iron-oak `reviews.reviews.items`, kept `reviews.testimonials.items`),
 * so the key differs per site — `findReviewItemsForPage` scans tolerantly.
 */
export interface ContentConfig {
  global?: Record<string, unknown>
  pages?: Record<string, Record<string, unknown>>
}

/** A `reviews.<key>.items` array is the canonical real-review source. */
function isReviewItemsKey(key: string): boolean {
  return /^reviews\..+\.items$/.test(key)
}

/**
 * Find the REAL review items for a page from `.dcs/content.yaml`, tolerant of
 * the per-site key naming (`reviews.reviews.items` vs `reviews.testimonials.
 * items`). Prefers a page-scoped block, then falls back to global; within a
 * block it picks the FIRST `reviews.*.items` array (sites carry one canonical
 * source). Returns `[]` when none is present — no synthesis.
 *
 * The honesty filter still runs downstream (`buildReviewSchemaParts`), so a
 * non-empty return here is NOT yet a guarantee of emission; items without a
 * rating/text/authorName are dropped there.
 */
export function findReviewItemsForPage(
  content: ContentConfig | undefined,
  pageSlug: string
): ReviewSource[] {
  if (!content) return []
  const fromBlock = (block: Record<string, unknown> | undefined): ReviewSource[] | null => {
    if (!block) return null
    for (const key of Object.keys(block)) {
      if (isReviewItemsKey(key) && Array.isArray(block[key])) {
        return block[key] as ReviewSource[]
      }
    }
    return null
  }
  const page = content.pages?.[pageSlug]
  return fromBlock(page) ?? fromBlock(content.global) ?? []
}

/** The flat content.yaml key holding a site's trade/occupational license. */
const BUSINESS_LICENSE_KEY = 'business.license'

/**
 * Read the free-text `business.license` content key from `.dcs/content.yaml`
 * (page-scoped block first, then global — mirroring `findReviewItemsForPage`;
 * NAP identity normally lives under `global`). Returns a trimmed non-empty
 * string, or `undefined` when the key is absent or empty. A bare numeric YAML
 * value (e.g. `business.license: 123456`) is coerced to its string form. No
 * synthesis — an unset key yields `undefined`, so the emit paths stay dark.
 */
export function findBusinessLicense(
  content: ContentConfig | undefined,
  pageSlug: string
): string | undefined {
  if (!content) return undefined
  const fromBlock = (block: Record<string, unknown> | undefined): string | undefined => {
    if (!block) return undefined
    const raw = block[BUSINESS_LICENSE_KEY]
    if (typeof raw === 'string') {
      const trimmed = raw.trim()
      return trimmed.length > 0 ? trimmed : undefined
    }
    if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
    return undefined
  }
  return fromBlock(content.pages?.[pageSlug]) ?? fromBlock(content.global)
}

// =============================================================================
// OG-URL absolutization
// =============================================================================

/**
 * Guarantee an absolute URL. When `value` is already absolute (`http(s)://`) it
 * is returned unchanged; when it is a site-relative path it is joined onto the
 * (trailing-slash-trimmed) `siteUrl`; when it is empty the `fallback` (typically
 * the already-absolute canonical) is returned.
 */
export function absolutizeUrl(
  value: string | undefined,
  siteUrl: string | undefined,
  fallback = ''
): string {
  const v = (value ?? '').trim()
  if (!v) return fallback
  if (/^https?:\/\//i.test(v)) return v
  const base = trimSlash(siteUrl ?? '')
  if (!base) return v
  return `${base}/${v.replace(/^\/+/, '')}`
}
